import { addRegionToPath } from 'lib/url/searchUrlUtils'
import offerPageURL from 'lib/offer/offerPageURL'
import { PROPERTY_MAP_MARKER_TYPE } from 'constants/map'
import config from 'constants/config'
import { OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL, OFFER_TYPE_TOUR } from 'constants/offer'

function hasLongitudeAndLatitude(location: { longitude: number, latitude: number }): boolean {
  return location.longitude !== null &&
    location.longitude !== undefined &&
    location.latitude !== undefined
}

function getMarkerKey(marker: App.MapMarker): string {
  return `${marker.latitude}_${marker.longitude}`
}

export function getPropertyOrTour(offer: App.Offer | App.OfferSummary): App.PackageTour | App.OfferProperty {
  if (!offer.lowestPricePackage) {
    return null
  }
  const lowestPackage = offer.lowestPricePackage
  return offer.parentType === OFFER_TYPE_TOUR ? lowestPackage.tour : offer.property
}

export function hasValidLocation(offer: App.Offer | App.OfferSummary): boolean {
  const propertyOrTour = getPropertyOrTour(offer)
  return propertyOrTour &&
    hasLongitudeAndLatitude(propertyOrTour)
}

export function getMarkersByOffers(offers: Array<App.Offer | App.OfferSummary>, regionCode: string): Array<App.MapMarker> {
  return formatMarkers(offers.filter(hasValidLocation).map(offer => getMarkerByOffer(offer, regionCode)))
}

export function getMarkerByOffer(offer: App.Offer | App.OfferSummary, regionCode: string): App.MapMarker {
  const propertyOrTour = getPropertyOrTour(offer)
  const latitude = parseFloat((propertyOrTour.latitude).toFixed(6))
  const longitude = parseFloat((propertyOrTour.longitude).toFixed(6))

  return {
    id: propertyOrTour.id,
    type: PROPERTY_MAP_MARKER_TYPE,
    latitude,
    longitude,
    imageId: offer.image.id,
    name: offer.name,
    subName: propertyOrTour.name,
    locationHeading: offer.locationHeading,
    locationSubheading: offer.locationSubheading,
    link: addRegionToPath(offerPageURL(offer), regionCode),
    propertyItem: {
      latitude,
      longitude,
      id: propertyOrTour.id,
      offerId: offer.id,
      continentCode: propertyOrTour.geoData.continentCode,
      countryCode: propertyOrTour.geoData.countryCode,
      country: propertyOrTour.geoData.country,
      administrativeAreaLevel1: propertyOrTour.geoData.administrativeAreaLevel1,
      administrativeAreaLevel2: propertyOrTour.geoData.administrativeAreaLevel2,
    },
  }
}

export function formatMarkers(markers: Array<App.MapMarker>): Array<App.MapMarker> {
  return markers.reduce((acc, marker, index) => {
    const updatedMarker = { ...marker }
    if (markers.filter(x => x.id === marker.id).length > 1) {
      updatedMarker.id = updatedMarker.id + '_' + index

      if (marker.type === PROPERTY_MAP_MARKER_TYPE) {
        updatedMarker.propertyItem = {
          ...updatedMarker.propertyItem,
          id: updatedMarker.id,
        }
      }
    }

    acc.push(updatedMarker)
    return acc
  }, [])
}

export function overlapMarkers(markers: Array<App.MapMarker>): Array<App.MapMarker> {
  const markerExists = new Map<string, Array<number>>()

  return markers.reduce((acc, marker) => {
    const key = getMarkerKey(marker)
    const updatedMarker = { ...marker }
    if (markers.filter(x => getMarkerKey(x) === key).length > 1) {
      let offsetData = markerExists.get(key) || [1, 0]

      offsetData[1] = offsetData[1] + 1

      if (offsetData[1] > 4) {
        offsetData = [offsetData[0] + 1, 1]
      }

      const offWithIt = 0.00001 * offsetData[0]

      switch (offsetData[1]) {
        case 1:
          updatedMarker.latitude = marker.latitude + offWithIt
          updatedMarker.longitude = marker.longitude + offWithIt
          break
        case 2:
          updatedMarker.latitude = marker.latitude - offWithIt
          updatedMarker.longitude = marker.longitude + offWithIt
          break
        case 3:
          updatedMarker.latitude = marker.latitude - offWithIt
          updatedMarker.longitude = marker.longitude - offWithIt
          break
        case 4:
          updatedMarker.latitude = marker.latitude + offWithIt
          updatedMarker.longitude = marker.longitude - offWithIt
          break
      }

      if (marker.type === PROPERTY_MAP_MARKER_TYPE) {
        updatedMarker.propertyItem = {
          ...updatedMarker.propertyItem,
          latitude: updatedMarker.latitude,
          longitude: updatedMarker.longitude,
        }
      }

      markerExists.set(key, offsetData)
    }

    acc.push(updatedMarker)
    return acc
  }, [])
}

function geoSortString(offer: App.Offer | App.OfferSummary): string {
  const propertyOrTour = getPropertyOrTour(offer)
  const geo = propertyOrTour.geoData
  return `${geo.continentCode || 'ZZ'},${geo.country},${geo.administrativeAreaLevel1},${geo.administrativeAreaLevel2},`
}

export function groupOffersGeographically(offers: Array<App.Offer | App.OfferSummary>): Array<App.Offer | App.OfferSummary> {
  return offers.filter(hasValidLocation).sort((a, b) => geoSortString(a).localeCompare(geoSortString(b)))
}

export function fitBoundsWithExtraSpace(map: google.maps.Map | null, bounds: google.maps.LatLngBounds): void {
  if (!map) {
    return
  }

  const minExtraSpace = 0.00001 // degrees of lat/lng
  const ne = bounds.getNorthEast()
  const sw = bounds.getSouthWest()
  const longitudeSpan = ne.lng() - sw.lng()
  const expansion = longitudeSpan > 200 ? 0.03 : 0.2
  const latExpansion = Math.max((ne.lat() - sw.lat()) * expansion, minExtraSpace)
  const lngExpansion = Math.max(longitudeSpan * expansion, minExtraSpace)
  const newNe = new google.maps.LatLng({ lat: ne.lat() + latExpansion, lng: ne.lng() + lngExpansion })
  const newSw = new google.maps.LatLng({ lat: sw.lat() - latExpansion, lng: sw.lng() - lngExpansion })
  const newBounds = new google.maps.LatLngBounds(newSw, newNe)
  map.fitBounds(newBounds)
}

const OFFER_TYPES_MARKER_ICON_IDS = {
  FLASH: 'tsap1ke1qvsrplhh5sd',
  BEDBANK: 'v6ai6wunmryst4vnvjal',
  LE_DEFAULT: 'ov59zv5vob79s77269k',
  DEFAULT: 'ka35kpvs8z43ect8axc',
}

export function getMapMarkerIconId(offerType: App.OfferType) {
  if (['luxuryescapes', 'lebusinesstraveller', 'leagenthub'].includes(config.BRAND)) {
    if (offerType === OFFER_TYPE_BED_BANK) return OFFER_TYPES_MARKER_ICON_IDS.BEDBANK
    if (offerType === OFFER_TYPE_HOTEL) return OFFER_TYPES_MARKER_ICON_IDS.FLASH

    return OFFER_TYPES_MARKER_ICON_IDS.LE_DEFAULT
  }

  return OFFER_TYPES_MARKER_ICON_IDS.DEFAULT
}
